import { makeAuthenticatedRequest } from './authService';

export interface Tag {
  name: string;
  color_code: string;
  created_at: string;
  updated_at: string;
}

class TagService {
  // Create a new tag
  async createTag(name: string, colorCode: string): Promise<{ success: boolean; message?: string; tag?: Tag }> {
    try {
      const response = await makeAuthenticatedRequest('/tags', {
        method: 'POST',
        body: JSON.stringify({
          name: name.trim(),
          color_code: colorCode
        })
      });
      
      return response;
    } catch (error) {
      console.error('Error creating tag:', error);
      throw error;
    }
  }

  // Get all tags
  async getAllTags(search?: string, limit?: number, offset?: number): Promise<{ success: boolean; tags?: Tag[]; count?: number }> {
    try {
      const queryParams = new URLSearchParams();
      if (search) queryParams.append('search', search);
      if (limit) queryParams.append('limit', limit.toString());
      if (offset) queryParams.append('offset', offset.toString());
      
      const queryString = queryParams.toString();
      const url = `/tags${queryString ? `?${queryString}` : ''}`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching tags:', error);
      throw error;
    }
  }

  // Get tag by name
  async getTagByName(name: string): Promise<{ success: boolean; tag?: Tag }> {
    try {
      const encodedName = encodeURIComponent(name);
      const response = await makeAuthenticatedRequest(`/tags/${encodedName}`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching tag:', error);
      throw error;
    }
  }

  // Update a tag
  async updateTag(name: string, newName?: string, colorCode?: string): Promise<{ success: boolean; message?: string; tag?: Tag }> {
    try {
      const encodedName = encodeURIComponent(name);
      const updateData: any = {};
      if (newName !== undefined) updateData.name = newName.trim();
      if (colorCode !== undefined) updateData.color_code = colorCode;
      
      const response = await makeAuthenticatedRequest(`/tags/${encodedName}`, {
        method: 'PUT',
        body: JSON.stringify(updateData)
      });
      
      return response;
    } catch (error) {
      console.error('Error updating tag:', error);
      throw error;
    }
  }

  // Delete a tag
  async deleteTag(name: string): Promise<{ success: boolean; message?: string }> {
    try {
      const encodedName = encodeURIComponent(name);
      const response = await makeAuthenticatedRequest(`/tags/${encodedName}`, {
        method: 'DELETE'
      });
      
      return response;
    } catch (error) {
      console.error('Error deleting tag:', error);
      throw error;
    }
  }

  // Get tags for a task (via relationships)
  async getTagsForTask(taskId: number): Promise<{ success: boolean; tags?: string[] }> {
    try {
      const response = await makeAuthenticatedRequest(`/relationships/from/${taskId}/task`, {
        method: 'GET'
      });
      
      // The relationships API wraps data in a 'data' property (from sendSuccess)
      const relationships = response.data?.relationships || response.relationships || [];
      
      if (response.success) {
        // Filter for tag relationships and extract tag names
        const tagNames = relationships
          .filter((rel: any) => rel.to_object_type === 'tag')
          .map((rel: any) => rel.to_object_id);
        
        console.log(`[TagService] Loaded ${tagNames.length} tags for task ${taskId}:`, tagNames);
        
        return {
          success: true,
          tags: tagNames
        };
      }
      
      return { success: true, tags: [] };
    } catch (error) {
      console.error('Error fetching tags for task:', error);
      throw error;
    }
  }

  // Add tags to a task
  async addTagsToTask(taskId: number, tagNames: string[]): Promise<{ success: boolean; message?: string }> {
    try {
      // Get current tags
      const currentTagsResponse = await this.getTagsForTask(taskId);
      const currentTags = currentTagsResponse.tags || [];
      
      // Merge with new tags (avoid duplicates)
      const allTags = [...new Set([...currentTags, ...tagNames])];
      
      // Sync all tags
      return await this.syncTaskTags(taskId, allTags);
    } catch (error) {
      console.error('Error adding tags to task:', error);
      throw error;
    }
  }

  // Remove tags from a task
  async removeTagsFromTask(taskId: number, tagNames: string[]): Promise<{ success: boolean; message?: string }> {
    try {
      // Get current tags
      const currentTagsResponse = await this.getTagsForTask(taskId);
      const currentTags = currentTagsResponse.tags || [];
      
      // Remove specified tags
      const remainingTags = currentTags.filter(tag => !tagNames.includes(tag));
      
      // Sync remaining tags
      return await this.syncTaskTags(taskId, remainingTags);
    } catch (error) {
      console.error('Error removing tags from task:', error);
      throw error;
    }
  }

  // Sync tags for a task (replace all tags)
  async syncTaskTags(taskId: number, tagNames: string[]): Promise<{ success: boolean; message?: string }> {
    try {
      // Delete all existing tag relationships for this task
      const currentTagsResponse = await this.getTagsForTask(taskId);
      const currentTags = currentTagsResponse.tags || [];
      
      // Remove all existing relationships
      for (const tagName of currentTags) {
        try {
          await makeAuthenticatedRequest(`/relationships/from/${taskId}/task/to/${encodeURIComponent(tagName)}/tag`, {
            method: 'DELETE'
          });
        } catch (error) {
          // Ignore errors if relationship doesn't exist
        }
      }
      
      // Create new relationships for provided tags
      for (const tagName of tagNames) {
        try {
          await makeAuthenticatedRequest('/relationships', {
            method: 'POST',
            body: JSON.stringify({
              from_object_id: taskId.toString(),
              from_object_type: 'task',
              to_object_id: tagName,
              to_object_type: 'tag'
            })
          });
        } catch (error) {
          console.error(`Error creating relationship for tag ${tagName}:`, error);
          // Continue with other tags even if one fails
        }
      }
      
      return { success: true, message: 'Tags synced successfully' };
    } catch (error) {
      console.error('Error syncing tags for task:', error);
      throw error;
    }
  }
}

export const tagService = new TagService();

